/**
 * CompanyManagement Page
 *
 * Super Admin page for managing companies with CRUD operations,
 * filtering, sorting, and pagination.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Building2,
  Plus,
  Search,
  Filter,
  RefreshCw,
  AlertCircle,
  X,
  ChevronLeft,
  Users,
} from 'lucide-react';
import { Navbar } from '../components/shared/Navbar';
import { LoadingState } from '../components/shared/LoadingState';
import { Card } from '../components/shared/Card';
import {
  CompanyTable,
  CreateCompanyModal,
  ConfirmDialog,
  Pagination,
  CompanyFeaturesModal,
} from '../components/admin';
import { superAdminApi } from '../services/adminApi';
import type {
  AdminCompany,
  AdminUser,
  CreateCompanyData,
  UpdateCompanyData,
  CompanyQueryParams,
  SubscriptionTier,
  CompanyStatus,
} from '../services/adminApi';

// Edit Company Modal (inline since it's simpler)
const EditCompanyModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (id: number, data: UpdateCompanyData) => Promise<void>;
  company: AdminCompany | null;
}> = ({ isOpen, onClose, onSubmit, company }) => {
  const [formData, setFormData] = useState<UpdateCompanyData>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (company && isOpen) {
      setFormData({
        name: company.name,
        domain: company.domain,
        subscriptionTier: company.subscriptionTier,
        maxUsers: company.maxUsers,
        status: company.status,
        billingEmail: company.billingEmail,
        contactPhone: company.contactPhone,
        address: company.address,
      });
      setError(null);
    }
  }, [company, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company) return;

    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit(company.id, formData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update company');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !company) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              Edit Company
            </h3>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Company ID</p>
                <p className="text-sm font-medium text-gray-900">#{company.id}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
                <input
                  type="text"
                  value={formData.domain || ''}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  placeholder="example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subscription</label>
                  <select
                    value={formData.subscriptionTier || 'free'}
                    onChange={(e) => setFormData({ ...formData, subscriptionTier: e.target.value as SubscriptionTier })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                  >
                    <option value="free">Free</option>
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Users</label>
                  <input
                    type="number"
                    value={formData.maxUsers || 5}
                    onChange={(e) => setFormData({ ...formData, maxUsers: parseInt(e.target.value, 10) })}
                    min="1"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status || 'active'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as CompanyStatus })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Email</label>
                <input
                  type="email"
                  value={formData.billingEmail || ''}
                  onChange={(e) => setFormData({ ...formData, billingEmail: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                <input
                  type="tel"
                  value={formData.contactPhone || ''}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Company Users Modal
const CompanyUsersModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  company: AdminCompany | null;
}> = ({ isOpen, onClose, company }) => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (company && isOpen) {
      setLoading(true);
      superAdminApi.getCompanyUsers(company.id)
        .then((response) => setUsers(response.data))
        .catch(() => {
          // Mock data
          setUsers([
            { id: 1, email: 'admin@company.com', role: 'company_admin', subscriptionTier: 'enterprise', status: 'active', createdAt: '2024-01-01' },
            { id: 2, email: 'user1@company.com', role: 'user', subscriptionTier: 'enterprise', status: 'active', createdAt: '2024-02-01' },
            { id: 3, email: 'user2@company.com', role: 'user', subscriptionTier: 'enterprise', status: 'inactive', createdAt: '2024-03-01' },
          ]);
        })
        .finally(() => setLoading(false));
    }
  }, [company, isOpen]);

  if (!isOpen || !company) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                {company.name} Users
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {company.usersCount} / {company.maxUsers} users
              </p>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {loading ? (
              <LoadingState message="Loading users..." size="sm" />
            ) : users.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No users in this company</p>
            ) : (
              <div className="space-y-3">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-semibold">
                          {user.email.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user.email}</p>
                        <p className="text-xs text-gray-500 capitalize">{user.role.replace('_', ' ')}</p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {user.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-between border-t border-gray-200 px-6 py-4">
            <button
              onClick={() => {
                onClose();
                navigate('/super-admin/users', { state: { companyFilter: company.id } });
              }}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              View in User Management
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CompanyManagement: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<AdminCompany[]>([]);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState<SubscriptionTier | ''>('');
  const [statusFilter, setStatusFilter] = useState<CompanyStatus | ''>('');
  const [showFilters, setShowFilters] = useState(false);

  // Sorting
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<AdminCompany | null>(null);

  // Check if we should open create modal from navigation state
  useEffect(() => {
    if (location.state?.openCreateModal) {
      setShowCreateModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // Fetch companies
  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);

    const params: CompanyQueryParams = {
      page: currentPage,
      limit: itemsPerPage,
      sortBy: sortBy as 'name' | 'createdAt' | 'usersCount',
      sortOrder,
    };

    if (searchQuery) params.search = searchQuery;
    if (tierFilter) params.subscriptionTier = tierFilter;
    if (statusFilter) params.status = statusFilter;

    try {
      const response = await superAdminApi.getAllCompanies(params);
      setCompanies(response.data);
      setTotalCompanies(response.total);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
      setError('Failed to load companies. Showing demo data.');
      // Mock data
      const mockCompanies: AdminCompany[] = [
        { id: 1, name: 'Acme Corporation', domain: 'acme.com', subscriptionTier: 'enterprise', usersCount: 45, maxUsers: 100, status: 'active', createdAt: '2024-01-15', billingEmail: 'billing@acme.com' },
        { id: 2, name: 'Tech Solutions Inc', domain: 'techsol.com', subscriptionTier: 'pro', usersCount: 12, maxUsers: 50, status: 'active', createdAt: '2024-02-20', billingEmail: 'accounts@techsol.com' },
        { id: 3, name: 'Freight Logistics LLC', domain: 'freightlog.com', subscriptionTier: 'basic', usersCount: 5, maxUsers: 10, status: 'active', createdAt: '2024-03-10' },
        { id: 4, name: 'Transport Pro', domain: 'transportpro.com', subscriptionTier: 'free', usersCount: 2, maxUsers: 5, status: 'inactive', createdAt: '2024-04-01' },
        { id: 5, name: 'Global Carriers', domain: 'globalcarriers.com', subscriptionTier: 'enterprise', usersCount: 78, maxUsers: 200, status: 'active', createdAt: '2024-01-05', billingEmail: 'finance@globalcarriers.com' },
      ];
      setCompanies(mockCompanies);
      setTotalCompanies(mockCompanies.length);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, sortBy, sortOrder, searchQuery, tierFilter, statusFilter]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Handlers
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchCompanies();
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setTierFilter('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handleCreateCompany = async (data: CreateCompanyData) => {
    try {
      await superAdminApi.createCompany(data);
      fetchCompanies();
    } catch (err) {
      throw err;
    }
  };

  const handleUpdateCompany = async (id: number, data: UpdateCompanyData) => {
    try {
      await superAdminApi.updateCompany(id, data);
      fetchCompanies();
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteCompany = async () => {
    if (!selectedCompany) return;
    try {
      await superAdminApi.deleteCompany(selectedCompany.id);
      fetchCompanies();
    } catch (err) {
      throw err;
    }
  };

  const handleEditClick = (company: AdminCompany) => {
    setSelectedCompany(company);
    setShowEditModal(true);
  };

  const handleDeleteClick = (company: AdminCompany) => {
    setSelectedCompany(company);
    setShowDeleteDialog(true);
  };

  const handleViewUsersClick = (company: AdminCompany) => {
    setSelectedCompany(company);
    setShowUsersModal(true);
  };

  const handleManageFeaturesClick = (company: AdminCompany) => {
    setSelectedCompany(company);
    setShowFeaturesModal(true);
  };

  const totalPages = Math.ceil(totalCompanies / itemsPerPage);
  const hasActiveFilters = !!searchQuery || !!tierFilter || !!statusFilter;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/super-admin')}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Building2 className="w-8 h-8 text-purple-600" />
                Company Management
              </h1>
              <p className="text-gray-600 mt-1">
                {totalCompanies.toLocaleString()} total companies
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchCompanies}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Company
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-800">{error}</p>
          </div>
        )}

        {/* Search and Filters */}
        <Card className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by company name or domain..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </form>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showFilters || hasActiveFilters
                  ? 'bg-purple-50 border-purple-200 text-purple-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="ml-1 px-1.5 py-0.5 bg-purple-600 text-white text-xs rounded-full">
                  {[searchQuery, tierFilter, statusFilter].filter(Boolean).length}
                </span>
              )}
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subscription</label>
                  <select
                    value={tierFilter}
                    onChange={(e) => {
                      setTierFilter(e.target.value as SubscriptionTier | '');
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                  >
                    <option value="">All Tiers</option>
                    <option value="free">Free</option>
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value as CompanyStatus | '');
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                  >
                    <option value="">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>
              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  className="mt-4 flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
                >
                  <X className="w-4 h-4" />
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </Card>

        {/* Companies Table */}
        {loading ? (
          <LoadingState message="Loading companies..." />
        ) : (
          <>
            <CompanyTable
              companies={companies}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
              onViewUsers={handleViewUsersClick}
              onManageFeatures={handleManageFeaturesClick}
              emptyMessage={
                hasActiveFilters
                  ? 'No companies match your filters'
                  : 'No companies found'
              }
            />

            {totalCompanies > 0 && (
              <div className="mt-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalCompanies}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(value) => {
                    setItemsPerPage(value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <CreateCompanyModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateCompany}
      />

      <EditCompanyModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedCompany(null);
        }}
        onSubmit={handleUpdateCompany}
        company={selectedCompany}
      />

      <CompanyUsersModal
        isOpen={showUsersModal}
        onClose={() => {
          setShowUsersModal(false);
          setSelectedCompany(null);
        }}
        company={selectedCompany}
      />

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedCompany(null);
        }}
        onConfirm={handleDeleteCompany}
        title="Delete Company"
        message={`Are you sure you want to delete "${selectedCompany?.name}"? This will also affect all ${selectedCompany?.usersCount || 0} users in this company. This action cannot be undone.`}
        confirmText="Delete Company"
        variant="danger"
        requireTyping={selectedCompany?.name}
      />

      <CompanyFeaturesModal
        isOpen={showFeaturesModal}
        onClose={() => {
          setShowFeaturesModal(false);
          setSelectedCompany(null);
        }}
        company={selectedCompany}
        onSave={fetchCompanies}
      />
    </div>
  );
};

export default CompanyManagement;
